import { Injectable } from '@angular/core';
import {
  BasicNotification,
  EsdcCardStatusChangeNotification,
  JobBegginingEndingNotification,
  JobNotification,
  LabelType,
  NotifikacijeClient,
  NotificationMessageType,
  SefHealthNotification,
  SessionTokenNotification,
  StatsChangedNotification,
  TableColumnIconDto,
} from '@kodit/core/data-api';
import { DynamicDialogConfig } from '@kodit/core/shared-ui';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as signalR from '@microsoft/signalr';
import { environment } from 'apps/core/src/environments/environment';
import { FiskalniRacunService } from 'libs/core/moduli/maloprodaja/src/lib/fiskalni-racun/fiskalni-racun.service';
import { DialogSize } from 'libs/core/shared-ui/src/lib/dialogs/dynamic-dialog/dynamicdialog-config';
import { SESSION_ID_NAME, TOKEN_NAME } from '..';
import { NotificationService } from './notification.service';
import { TokenService } from './security/token.service';
import { SefService } from './sef.service';
import { RatingService } from './rating.service';
import { ProgressService } from 'libs/core/core-ui/src/lib/progress.service';
import { AlertService } from './alert.service';

@Injectable({
  providedIn: 'root',
})
export class SignalrService {
  /** Props */
  dialogConfig: DynamicDialogConfig = new DynamicDialogConfig(DialogSize.SMALL);

  private _hubConnection: signalR.HubConnection;
  private _manuallyStopped = false;
  private _reconnectTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private _notificationService: NotificationService,
    private _sefService: SefService,
    private _tokenService: TokenService,
    private _ratingService: RatingService,
    private _progressService: ProgressService,
    private _fiskalniService: FiskalniRacunService,
    private _alertService: AlertService,
    private _notifikacijeClient: NotifikacijeClient,
    private _jwtHelper: JwtHelperService
  ) {}

  public startConnection = (tenantKey: string, token: string) => {
    // (
    //   `Client [${tenantKey}] starting connection for notifications...`
    // );
    this._manuallyStopped = false;
    this._hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.apiBaseUrl}/notifications`, {
        // token je citan iz localStorage pri svakom (re)connect pokusaju,
        // a ne zarobljen u closure-u - inace retry petlja ispod zauvek
        // salje istekli token koji je vazio u trenutku prve konekcije
        accessTokenFactory: () => localStorage.getItem(TOKEN_NAME) ?? '',
      })
      .withAutomaticReconnect()
      .build();

    this._hubConnection.onreconnecting((error) => {
      console.warn('[SignalR] Reconnecting...', error);
    });

    this._hubConnection.onreconnected(() => {
      console.info('[SignalR] Reconnected - rehidriram stanje progresa');
      this._rehydrateProgress();
    });

    this._hubConnection.onclose((error) => {
      console.warn('[SignalR] Konekcija zatvorena', error);
      if (!this._manuallyStopped) {
        this._scheduleReconnect(tenantKey, token);
      }
    });

    this._hubConnection
      .start()
      .then(() => {
        this._addNotificationListener();
        this._rehydrateProgress();
      })
      .catch((error) => {
        console.error('[SignalR] Neuspesno pokretanje konekcije', error);
        this._scheduleReconnect(tenantKey, token);
      });
  };

  // Token se osvezava negde drugde (interceptor/AuthGuard na obicnom API pozivu), SignalR konekcija
  // to samo pasivno prati preko accessTokenFactory. Ako je tab neaktivan i nista ne triggeruje refresh,
  // stari kod je ovde gadjao /notifications/negotiate na svakih 5s zauvek sa isteklim tokenom - beskonacna
  // autentifikaciona greska na backendu. Zato ovde lokalno proveravamo istek tokena pre svakog pokusaja:
  // ako je istekao, samo cekamo (bez pogadjanja servera) dok se u localStorage-u ne pojavi svez token.
  private _scheduleReconnect(tenantKey: string, token: string) {
    this._reconnectTimer = setTimeout(() => {
      if (this._manuallyStopped) {
        return;
      }
      const currentToken = localStorage.getItem(TOKEN_NAME);
      if (!currentToken || this._jwtHelper.isTokenExpired(currentToken)) {
        this._scheduleReconnect(tenantKey, token);
        return;
      }
      this.startConnection(tenantKey, token);
    }, 5000);
  }

  public closeConnection() {
    clearTimeout(this._reconnectTimer);
    if (this._hubConnection) {
      this._manuallyStopped = true;
      this._hubConnection.stop().then();
      this._hubConnection = undefined;
    }
  }

  // Poziva se posle svakog uspesnog (re)connect-a - i posle inicijalnog start()-a, i posle SignalR-ovog
  // ugradjenog automatskog reconnect-a (onreconnected) koji NE prolazi kroz startConnection()/.then().
  // Bez ovoga, izgubljena begin/progress/end poruka tokom prekida konekcije bi ostavila progress bar
  // trajno zaglavljenim ili ga nikad ne bi prikazala.
  private _rehydrateProgress = () => {
    this._notifikacijeClient.getAktivniJobovi().subscribe({
      next: (result) => this._progressService.reconcileWithServer(result.data ?? []),
      error: (err) =>
        console.error('[SignalR] Neuspesno povlacenje aktivnih jobova posle (re)connect-a', err),
    });
  };

  private _addNotificationListener = () => {
    this._hubConnection.on(
      'NOTIFICATION_FROM_SERVER',
      (type: NotificationMessageType, data: any) => {
        switch (type) {
          case NotificationMessageType.STATUS_CHANGE:
            const statusChangeData: StatsChangedNotification =
              data as StatsChangedNotification;
            this._notificationService.addTopbarNotifications([
              {
                id: statusChangeData.id,
                jeProcitana: false,
                naslov: statusChangeData.title,
                tekst: statusChangeData.message,
                iconDto: new TableColumnIconDto({
                  backgroundColor: statusChangeData.backgroundColor,
                  iconColor: statusChangeData.iconColor,
                  iconName: statusChangeData.iconName,
                }),
                actionUrl: statusChangeData.actionUrl,
              },
            ]);
            break;

          case NotificationMessageType.JOB_BEGGINING_ENDING:
            const jobBegginingEndingData = data as JobBegginingEndingNotification;
            if (jobBegginingEndingData.jePocetak) {
              this._progressService.addProgresses(
                jobBegginingEndingData.vrstaJoba
              );
            } else {
              this._progressService.deleteProgress(
                jobBegginingEndingData.vrstaJoba
              );
            }
            break;

          case NotificationMessageType.JOB:
            const jobData = data as JobNotification;
            this._progressService.updateProgress(
              jobData.vrstaJoba,
              jobData.progress
            );
            if (jobData.message) {
              // Label opisuje stvarni ishod posla (npr. delimican/neuspesan import) - ranije se ovde uvek
              // zvao addSuccessMsg (zelena poruka) bez obzira na sadrzaj, sto je davalo pogresan utisak
              // korisniku da je import uspeo kad nije (ClickUp 869f2bw9d).
              switch (jobData.label) {
                case LabelType.Error:
                  this._alertService.addFailedMsg(jobData.message);
                  break;
                case LabelType.Warning:
                  this._alertService.addWarnMsg(jobData.message);
                  break;
                default:
                  this._alertService.addSuccessMsg(jobData.message);
                  break;
              }
            }
            break;

          case NotificationMessageType.BASIC:
            const basicData = data as BasicNotification;

            this._notificationService.addTopbarNotifications([
              {
                id: basicData.id,
                jeProcitana: false,
                naslov: 'Isticanje sertifikata',
                tekst: basicData.message,
                iconDto: new TableColumnIconDto({
                  backgroundColor: '#1976d2',
                  iconColor: '#ffffff',
                  iconName: 'pi pi-info-circle',
                }),
                actionUrl: '/podesavanja/fiskalna-kasa',
              },
            ]);
            break;

          case NotificationMessageType.SEF_ACTIVITY:
            const sefActivityData = data as SefHealthNotification;
            this._sefService.setIsSefActive = {
              isActive: sefActivityData.isActive,
              background: sefActivityData.backgroundColor,
            };
            break;

          case NotificationMessageType.SESSION_TOKEN_VALIDITY:
          const sessionToken = data as SessionTokenNotification;
            this._tokenService.setShowLogoutDialog =
              sessionToken.sessionId !== localStorage.getItem(SESSION_ID_NAME);
            break;

          // case NotificationMessageType.ESDC_CARD_STATUS_CHANGE:
          //   const esdcCardStatusData = data as EsdcCardStatusChangeNotification;
          //   this._fiskalniService.setEsdcCardStatus = esdcCardStatusData;
          //   break;

          case NotificationMessageType.RATING:
            this._ratingService.setCheckUserRating = true;
            break;

          default:
            return;
        }
      }
    );
  };
}
